import type { ErrorReporter } from '@estimating/error-reporting';
import {
  createSsoClient,
  type SsoClient,
  type SsoClientOptions,
  type SsoError,
} from '@estimating/sso';
import { AuthError, type Authenticator } from './authenticator';

/** How long the first page waits for single sign-on before it shows anyway. */
const FIRST_SYNC_WAIT_MS = 3_000;

/**
 * The client for single sign-on with the brand's shop management system (D-060), over
 * this app's Firebase sign-in.
 */
export function createSingleSignOn(
  authenticator: Authenticator,
  settings: Pick<SsoClientOptions, 'apiBaseUrl' | 'projectId' | 'cookieDomain'>,
  errorReporter: ErrorReporter,
): SsoClient {
  return createSsoClient({
    ...settings,
    session: {
      currentUid: () => authenticator.currentUser()?.uid ?? null,
      idToken: () => authenticator.idToken({ forceRefresh: false }),
      signInWithCustomToken: authenticator.signInWithCustomToken,
      signOut: authenticator.signOut,
    },
    onError: (error) => {
      reportSsoError(error, errorReporter);
    },
  });
}

/**
 * Single sign-on fixes itself once the network or the API is back, and the API reports
 * its own outages, so neither is reported here. A fresh error is reported rather than
 * this one, whose causes may carry Firebase's messages, which can contain the email.
 */
export function reportSsoError(error: SsoError, errorReporter: ErrorReporter): void {
  if (error.kind === 'network' || error.status === 503) {
    return;
  }
  const authCode = error.cause instanceof AuthError ? { authCode: error.cause.code } : {};
  errorReporter.captureException(new Error(`Single sign-on: ${error.message}`), {
    tags: { feature: 'sso', ssoKind: error.kind, ...authCode },
  });
}

function withinTime(work: Promise<void>, timeoutMs: number): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<void>((resolve) => {
    timer = setTimeout(resolve, timeoutMs);
  });
  return Promise.race([work, timeout]).finally(() => {
    clearTimeout(timer);
  });
}

/**
 * Signs in whoever is signed in to the brand's platform in this browser, and shares
 * this app's own sign-ins and sign-outs with it. The rest of the app uses the
 * authenticator as before; `sso.watch()` keeps them in step while the app is open.
 */
export function withSingleSignOn(
  authenticator: Authenticator,
  sso: Pick<SsoClient, 'sync' | 'signIn' | 'signOut'>,
  { firstSyncWaitMs = FIRST_SYNC_WAIT_MS }: { readonly firstSyncWaitMs?: number } = {},
): Authenticator {
  let ready: Promise<void> | undefined;
  return {
    ...authenticator,
    // Before the first page shows, so someone signed in to the platform isn't sent to
    // sign in here. A slow API only delays it so long; the sync carries on, and the
    // route guards run again when it signs someone in (App.tsx).
    ready: () =>
      (ready ??= authenticator.ready().then(() => withinTime(sso.sync(), firstSyncWaitMs))),
    signInWithEmailAndPassword: (email, password) =>
      sso.signIn(() => authenticator.signInWithEmailAndPassword(email, password)),
    signOut: () => sso.signOut(),
  };
}
